<template>
  <div class="popular-question-management">
    <ds-header title="热门问题管理"></ds-header>

    <a-form layout="inline">
      <a-form-item label="问题内容：">
        <a-input
          v-model.trim="searchForm.question"
          placeholder="请输入问题内容"
          allow-clear
          class="inputClass"
          @pressEnter="search"
        />
      </a-form-item>
      <a-form-item label="状态：">
        <a-select
          v-model="searchForm.status"
          placeholder="全部状态"
          allow-clear
          style="width: 200px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="适用项目：">
        <a-tree-select
          v-model="searchForm.projectId"
          style="width: 200px"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          placeholder="全部项目"
          allow-clear
          tree-default-expand-all
          :tree-data="searchProjectTreeData"
        />
      </a-form-item>
      <span>
        <a-button type="primary" @click="search" style="margin-top: 3px">
          <a-icon type="search" />查询
        </a-button>
        <a-button style="margin-left: 15px" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>

    <div style="padding-top: 15px">
      <a-button type="primary" @click="openAddModal">
        <a-icon type="plus" />新增热门问题
      </a-button>
    </div>

    <a-table
      :rowKey="(record, index) => record.id || index"
      :columns="table.columns"
      :data-source="table.dataInfo"
      :pagination="table.pagination.total ? table.pagination : false"
      :loading="table.loading"
      @change="pageChange"
      bordered
      style="margin-top: 15px"
    >
      <span slot="sortNum" slot-scope="text">
        <a-tag color="blue">{{ text }}</a-tag>
      </span>
      <span slot="status" slot-scope="text">
        <a-tag :color="text === 1 ? 'green' : 'red'">
          {{ text === 1 ? '已启用' : '已禁用' }}
        </a-tag>
      </span>
      <span slot="projectScope" slot-scope="text, record">
        <span v-if="record.projectScope === 1">全部项目</span>
        <a-tooltip v-else-if="record.projectConfigs && record.projectConfigs.length">
          <template slot="title">{{ formatProjectNames(record.projectConfigs) }}</template>
          <span class="ellipsis-text">{{ formatProjectScopeText(record.projectConfigs) }}</span>
        </a-tooltip>
        <span v-else>-</span>
      </span>
      <span slot="createTime" slot-scope="text">
        <div class="time-cell">{{ formatDate(text) }}</div>
        <div class="time-cell time-sub">{{ formatTime(text) }}</div>
      </span>
      <span slot="updateTime" slot-scope="text">
        <div class="time-cell">{{ formatDate(text) }}</div>
        <div class="time-cell time-sub">{{ formatTime(text) }}</div>
      </span>
      <span slot="action" slot-scope="text, record" class="action">
        <a-switch
          :checked="record.status === 1"
          @change="(checked) => handleStatusChange(record, checked)"
        />
        <a-button class="btnClass" type="link" @click="openEditModal(record)">编辑</a-button>
        <a-button class="btnClass delete-btn" type="link" @click="handleDelete(record)">删除</a-button>
      </span>
    </a-table>

    <a-modal
      :maskClosable="false"
      :title="modalTitle"
      :visible="modalVisible"
      :confirm-loading="confirmLoading"
      width="720px"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    >
      <a-spin :spinning="modalLoading">
        <a-form-model
          ref="form"
          :model="form"
          :rules="rules"
          :label-col="{ span: 6 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-form-model-item label="问题内容" prop="question">
            <a-textarea
              v-model="form.question"
              placeholder="请输入问题内容"
              :rows="3"
              :maxLength="500"
            />
          </a-form-model-item>
          <a-form-model-item label="项目配置" prop="projectScope">
            <a-radio-group v-model="form.projectScope" @change="handleProjectScopeChange">
              <a-radio v-for="item in scopeOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-form-model-item v-if="form.projectScope === 1" label="答案内容" prop="answer">
            <a-textarea
              v-model="form.answer"
              placeholder="请输入答案内容"
              :rows="4"
              :maxLength="500"
            />
          </a-form-model-item>
          <template v-else>
            <a-form-model-item
              v-for="(item, index) in form.projectConfigs"
              :key="'config-' + index"
              :label="index === 0 ? '项目答案' : ' '"
              :colon="index === 0"
            >
              <div class="project-config-row">
                <a-form-model-item
                  class="config-project"
                  :prop="'projectConfigs.' + index + '.projectId'"
                  :rules="projectIdRules"
                  :wrapper-col="{ span: 24 }"
                >
                  <a-select
                    v-model="item.projectId"
                    placeholder="请选择项目"
                    show-search
                    allow-clear
                    :filter-option="filterProjectOption"
                    :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                  >
                    <a-select-option v-for="p in getSelectableProjects(index)" :key="p.id" :value="p.id">
                      {{ p.project_name }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
                <a-form-model-item
                  class="config-answer"
                  :prop="'projectConfigs.' + index + '.answer'"
                  :rules="projectAnswerRules"
                  :wrapper-col="{ span: 24 }"
                >
                  <a-textarea
                    v-model="item.answer"
                    placeholder="请输入该项目的答案"
                    :rows="2"
                    :maxLength="500"
                  />
                </a-form-model-item>
                <a-button
                  type="link"
                  class="delete-btn"
                  :disabled="form.projectConfigs.length <= 1"
                  @click="removeProjectConfig(index)"
                >删除</a-button>
              </div>
            </a-form-model-item>
            <a-form-model-item label=" " :colon="false">
              <a-button
                type="dashed"
                :disabled="form.projectConfigs.length >= maxProjectConfigs"
                @click="addProjectConfig"
              >
                <a-icon type="plus" />新增项目
              </a-button>
            </a-form-model-item>
          </template>
          <a-form-model-item label="排序值" prop="sortNum">
            <a-input-number
              v-model="form.sortNum"
              placeholder="请输入排序值，数值越大越靠前"
              :min="0"
              :max="9999"
              style="width: 100%"
            />
          </a-form-model-item>
          <a-form-model-item label="状态" prop="status">
            <a-select v-model="form.status" placeholder="请选择状态">
              <a-select-option v-for="item in formStatusOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  getPopularQuestionList,
  addPopularQuestion,
  updatePopularQuestion,
  updatePopularQuestionStatus,
  getPopularQuestionDetail,
  deletePopularQuestion,
} from "@/api/intelligentCustomerService";
import {
  POPULAR_QUESTION_STATUS,
  POPULAR_QUESTION_PROJECT_SCOPE,
  POPULAR_QUESTION_SCOPE,
  POPULAR_QUESTION_MAX_PROJECT_CONFIGS,
} from "../help";

const getDefaultForm = () => ({
  id: undefined,
  question: "",
  // scope=1 的统一答案；切到指定项目时保留草稿，提交时由 buildSavePayload 置 null
  answer: "",
  projectScope: POPULAR_QUESTION_SCOPE.ALL,
  projectConfigs: [],
  sortNum: 0,
  status: 1,
});

const getDefaultProjectConfig = () => ({ projectId: undefined, answer: "" });

export default {
  name: "popularQuestionManagement",
  data() {
    const validateProjectNotDuplicate = (rule, value, callback) => {
      if (!value) return callback();
      const index = Number(rule.field.split(".")[1]);
      const duplicate = this.form.projectConfigs.some(
        (config, i) => i !== index && String(config.projectId) === String(value)
      );
      if (duplicate) return callback(new Error("该项目已配置答案，请勿重复选择"));
      callback();
    };
    return {
      statusOptions: POPULAR_QUESTION_STATUS,
      formStatusOptions: POPULAR_QUESTION_STATUS,
      scopeOptions: POPULAR_QUESTION_PROJECT_SCOPE,
      maxProjectConfigs: POPULAR_QUESTION_MAX_PROJECT_CONFIGS,
      searchForm: {
        question: "",
        status: undefined,
        projectId: undefined,
      },
      table: {
        loading: false,
        columns: [
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
          },
          {
            title: "问题内容",
            dataIndex: "question",
            key: "question",
            ellipsis: true,
            width: 320,
          },
          {
            title: "适用项目",
            dataIndex: "projectScope",
            key: "projectScope",
            width: 260,
            scopedSlots: { customRender: "projectScope" },
          },
          {
            title: "排序值",
            dataIndex: "sortNum",
            key: "sortNum",
            width: 120,
            scopedSlots: { customRender: "sortNum" },
          },
          {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            scopedSlots: { customRender: "status" },
          },
          {
            title: "创建时间",
            dataIndex: "createTime",
            key: "createTime",
            width: 160,
            scopedSlots: { customRender: "createTime" },
          },
          {
            title: "更新时间",
            dataIndex: "updateTime",
            key: "updateTime",
            width: 160,
            scopedSlots: { customRender: "updateTime" },
          },
          {
            title: "操作",
            key: "action",
            width: 220,
            scopedSlots: { customRender: "action" },
          },
        ],
        dataInfo: [],
        pagination: {
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
      modalVisible: false,
      confirmLoading: false,
      modalLoading: false,
      modalType: "add",
      form: getDefaultForm(),
      rules: {
        question: [{ required: true, message: "请输入问题内容", trigger: "blur" }],
        answer: [{ required: true, message: "请输入答案内容", trigger: "blur" }],
        sortNum: [{ required: true, message: "请输入排序值", trigger: "change" }],
        status: [{ required: true, message: "请选择状态", trigger: "change" }],
      },
      projectIdRules: [
        { required: true, message: "请选择项目", trigger: "change" },
        { validator: validateProjectNotDuplicate, trigger: "change" },
      ],
      projectAnswerRules: [
        { required: true, message: "请输入该项目的答案", trigger: "blur" },
        { max: 500, message: "答案最多500个字符", trigger: "blur" },
      ],
    };
  },
  computed: {
    ...mapState({
      projectList: (state) => state.common.projects || [],
    }),
    flatProjectList() {
      const result = [];
      const walk = (items) => {
        (items || []).forEach((item) => {
          result.push({
            id: String(item.id),
            project_name: item.project_name,
          });
          if (item.child && item.child.length) {
            walk(item.child);
          }
        });
      };
      walk(this.projectList);
      return result;
    },
    searchProjectTreeData() {
      const mapNode = (item) => ({
        value: String(item.id),
        key: String(item.id),
        title: item.project_name,
        disabled: !!(item.child && item.child.length),
        children: (item.child || []).map(mapNode),
      });
      return this.projectList.map(mapNode);
    },
    modalTitle() {
      return this.modalType === "add" ? "新增热门问题" : "编辑热门问题";
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    formatDate(time) {
      if (!time) return "-";
      return String(time).split(" ")[0] || time;
    },
    formatTime(time) {
      if (!time) return "";
      const parts = String(time).split(" ");
      return parts[1] || "";
    },
    formatProjectNames(configs = []) {
      return configs.map((config) => config.projectName || "-").join("、");
    },
    formatProjectScopeText(configs = []) {
      const previewCount = 2;
      const names = configs
        .slice(0, previewCount)
        .map((config) => config.projectName || "-");
      const rest = configs.length - names.length;
      return names.join("、") + (rest > 0 ? ` +${rest}` : "");
    },
    search() {
      this.table.pagination.current = 1;
      this.fetchList();
    },
    resetSearchForm() {
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.searchForm = this.$options.data.call(this).searchForm;
      this.fetchList();
    },
    pageChange(option) {
      this.table.pagination.current = option.current;
      this.table.pagination.pageSize = option.pageSize;
      this.fetchList();
    },
    async fetchList() {
      this.table.loading = true;
      try {
        const params = {
          question: this.searchForm.question,
          status: this.searchForm.status,
          projectId: this.searchForm.projectId || null,
          pageNo: this.table.pagination.current,
          pageSize: this.table.pagination.pageSize,
        };
        const res = await getPopularQuestionList(params);
        if (this.isSuccess(res)) {
          if (Array.isArray(res.data)) {
            this.table.dataInfo = res.data;
            this.table.pagination.total = res.data.length;
          } else {
            this.table.dataInfo = res.data?.list || res.data?.records || [];
            this.table.pagination.total = res.data?.total || 0;
          }
        }
      } catch {
        this.$message.error("获取列表失败");
      } finally {
        this.table.loading = false;
      }
    },
    resetForm() {
      this.form = getDefaultForm();
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    openAddModal() {
      this.modalType = "add";
      this.resetForm();
      this.modalVisible = true;
    },
    async openEditModal(record) {
      this.modalType = "edit";
      this.resetForm();
      this.modalVisible = true;
      this.modalLoading = true;
      try {
        const res = await getPopularQuestionDetail(record.id);
        if (this.isSuccess(res) && res.data) {
          this.fillFormFromDetail(res.data);
        } else {
          this.$message.error(res?.msg || "获取详情失败");
          this.modalVisible = false;
        }
      } catch {
        this.$message.error("获取详情失败");
        this.modalVisible = false;
      } finally {
        this.modalLoading = false;
      }
    },
    fillFormFromDetail(data) {
      const scope = data.projectScope || POPULAR_QUESTION_SCOPE.ALL;
      const configs = Array.isArray(data.projectConfigs) ? data.projectConfigs : [];
      const projectConfigs =
        scope === POPULAR_QUESTION_SCOPE.CUSTOM
          ? configs.map((config) => ({
              projectId: config.projectId != null ? String(config.projectId) : undefined,
              answer: config.answer || "",
            }))
          : [];
      if (scope === POPULAR_QUESTION_SCOPE.CUSTOM && projectConfigs.length === 0) {
        projectConfigs.push(getDefaultProjectConfig());
      }
      this.form = {
        id: data.id,
        question: data.question || "",
        projectScope: scope,
        answer: data.answer || "",
        projectConfigs,
        sortNum: data.sortNum != null ? data.sortNum : 0,
        status: data.status != null ? data.status : 1,
      };
    },
    handleModalCancel() {
      this.modalVisible = false;
      this.resetForm();
    },
    buildSavePayload() {
      const isAllProject = this.form.projectScope === POPULAR_QUESTION_SCOPE.ALL;
      const payload = {
        question: this.form.question,
        projectScope: this.form.projectScope,
        answer: isAllProject ? this.form.answer : null,
        projectConfigs: isAllProject
          ? []
          : this.form.projectConfigs.map((config) => ({
              projectId: String(config.projectId),
              answer: config.answer,
            })),
        sortNum: this.form.sortNum,
        status: this.form.status,
      };
      if (this.modalType === "edit") {
        payload.id = this.form.id;
      }
      return payload;
    },
    handleModalOk() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        this.confirmLoading = true;
        try {
          const payload = this.buildSavePayload();
          const api = this.modalType === "edit" ? updatePopularQuestion : addPopularQuestion;
          const res = await api(payload);
          if (this.isSuccess(res)) {
            this.$message.success(res.msg || "保存成功");
            this.handleModalCancel();
            this.fetchList();
          } else {
            this.$message.error(res?.msg || "保存失败");
          }
        } catch {
          this.$message.error("保存失败");
        } finally {
          this.confirmLoading = false;
        }
      });
    },
    addProjectConfig() {
      if (this.form.projectConfigs.length >= this.maxProjectConfigs) return;
      this.form.projectConfigs.push(getDefaultProjectConfig());
    },
    removeProjectConfig(index) {
      this.form.projectConfigs.splice(index, 1);
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    getSelectableProjects(index) {
      const selectedIds = this.form.projectConfigs
        .filter((_, i) => i !== index)
        .map((config) => String(config.projectId));
      return this.flatProjectList.filter((project) => !selectedIds.includes(project.id));
    },
    handleProjectScopeChange() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    filterProjectOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    handleStatusChange(record, checked) {
      const status = checked ? 1 : 0;
      const actionText = checked ? "启用" : "禁用";
      const that = this;
      this.$confirm({
        title: `确认${actionText}该热门问题？`,
        onOk() {
          return updatePopularQuestionStatus(record.id, status)
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || `${actionText}成功`);
                that.fetchList();
              } else {
                that.$message.error(res?.msg || `${actionText}失败`);
              }
            })
            .catch(() => {
              that.$message.error(`${actionText}失败`);
            });
        },
      });
    },
    handleDelete(record) {
      const that = this;
      this.$confirm({
        title: "确认删除该热门问题？",
        onOk() {
          return deletePopularQuestion(record.id)
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || "删除成功");
                that.fetchList();
              } else {
                that.$message.error(res?.msg || "删除失败");
              }
            })
            .catch(() => {
              that.$message.error("删除失败");
            });
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.popular-question-management {
  // 列宽合计较大，小屏时在表格内部横向滚动，避免撑开页面
  ::v-deep .ant-table {
    overflow-x: auto;

    table {
      min-width: 1450px;
    }
  }

  .time-cell {
    line-height: 1.4;
  }

  .time-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .delete-btn {
    color: #ff4d4f;
  }

  .ellipsis-text {
    display: inline-block;
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }

  .project-config-row {
    display: flex;
    align-items: flex-start;

    .config-project {
      width: 220px;
      flex-shrink: 0;
      margin-right: 8px;
    }

    .config-answer {
      flex: 1;
      margin-right: 8px;
    }

    .ant-form-item {
      margin-bottom: 0;
    }
  }
}
</style>
